import React from 'react';

/**
 * Paginated list of referred users
 * @param {Object} props
 * @param {Array} props.referrals - List of referral objects
 * @param {Object} props.pagination - Pagination info
 * @param {Function} props.onPageChange - Callback when page changes
 * @param {Object} props.translations - Translation strings
 * @param {string} props.railsEnv - Rails environment (for test IDs)
 */
const ReferredUsersList = ({ referrals, pagination, onPageChange, translations, railsEnv }) => {
  const isTestMode = railsEnv === 'test' || railsEnv === 'development';
  const t = translations?.referred_users || {};
  const tPagination = translations?.pagination || {};

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="badge bg-success bg-opacity-10 text-success mb-1">{t.active}</span>;
      case 'pending':
        return <span className="badge bg-warning bg-opacity-10 text-warning mb-1">{t.pending}</span>;
      default:
        return <span className="badge bg-secondary bg-opacity-10 text-secondary mb-1">{t.inactive}</span>;
    }
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    for (let i = 1; i <= pagination.total_pages; i++) {
      buttons.push(
        <button
          key={i}
          className={`btn btn-sm ${i === pagination.current_page ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => onPageChange(i)}
          disabled={i === pagination.current_page}
        >
          {i}
        </button>
      );
    }
    return buttons;
  };

  return (
    <div className="col-lg-8">
      <div className="card border rounded-3">
        <div className="card-body p-4">
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom">
            <h3 className="h5 fw-bold mb-0">{t.title}</h3>
            <select className="form-select form-select-sm" style={{ width: 'auto' }}>
              <option>{t.all_status}</option>
              <option>{t.active}</option>
              <option>{t.pending}</option>
              <option>{t.inactive}</option>
            </select>
          </div>

          {/* User List */}
          <div className="mb-3" {...(isTestMode && { 'data-testid': 'referrals-list' })}>
            {referrals.length > 0 ? (
              referrals.map((referral, index) => (
                <div
                  key={referral.id}
                  className={`d-flex align-items-center justify-content-between py-3 ${index > 0 ? 'border-top' : ''}`}
                >
                  <div className="d-flex align-items-center">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle me-3"
                      style={{
                        width: '40px',
                        height: '40px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      }}
                    >
                      <i className="bi bi-person-fill text-white"></i>
                    </div>
                    <div>
                      <h5 className="fw-semibold mb-1">{referral.referred_user_name}</h5>
                    </div>
                  </div>
                  <div className="text-end">
                    {getStatusBadge(referral.status)}
                    <p className="text-muted small mb-0">
                      {t.joined} {formatDate(referral.joined_at)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-5">
                <i className="bi bi-people text-muted mb-3" style={{ fontSize: '48px' }}></i>
                <p className="text-muted">{t.empty_state}</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="d-flex align-items-center justify-content-between pt-3 border-top">
              <p className="text-muted small mb-0">
                {tPagination.showing} {pagination.offset + 1}-{Math.min(pagination.offset + pagination.size, pagination.total_count)} {tPagination.of} {pagination.total_count} {tPagination.referrals}
              </p>
              <div className="d-flex gap-2" {...(isTestMode && { 'data-testid': 'pagination-controls' })}>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => onPageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                >
                  {tPagination.previous}
                </button>
                {renderPaginationButtons()}
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => onPageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.total_pages}
                >
                  {tPagination.next}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferredUsersList;
